import { abs } from "~/tools/abs";
import { floor } from "~/tools/floor";
import { max } from "~/tools/max";
import { pow } from "~/tools/pow";
import { round } from "~/tools/round";
import { log10 } from "~/tools/trigonometry";

import type { PrecisionCount, UnitFormatter, UnitFormatterWithOptions } from "./_types";

const getDecimalsForValue = (value: number): number => {
	if (value % 1 === 0) {
		return 0;
	}

	const unsignedValue = abs(value);
	const dec = 1 - floor(log10(unsignedValue), 0);
	const magnitude = pow(10, -dec);
	const norm = unsignedValue / magnitude; // norm is between 1.0 and 10.0

	return max(0, norm > 2.25 ? dec + 1 : dec);
};

export const toFixed = (value: number, precision?: PrecisionCount): string => {
	if (!isFinite(value)) {
		return value.toLocaleString();
	}

	const finalPrecision = max(0, precision ?? getDecimalsForValue(value));

	if (value === 0) {
		return value.toFixed(finalPrecision);
	}

	const factor = pow(10, max(0, finalPrecision));
	const formatted = String(round(value * factor, 0) / factor);

	// if exponent return directly
	if (formatted.includes("e")) {
		return formatted;
	}

	const [whole, decimals = ""] = formatted.split(".");

	if (finalPrecision === decimals.length) {
		return formatted;
	}

	return `${whole}.${decimals.padEnd(finalPrecision, "0")}`;
};

export const toFixedUnit =
	(unit: string, asPrefix?: boolean): UnitFormatterWithOptions =>
	(size, { precision } = {}) => {
		const text = toFixed(size, precision);

		if (!unit) {
			return text;
		}

		if (asPrefix) {
			return `${unit}${text}`;
		}

		return `${text} ${unit}`;
	};

export const locale: UnitFormatterWithOptions = (value, { precision } = {}) =>
	value.toLocaleString(undefined, { maximumFractionDigits: precision ?? undefined });

export const stringFormatter: UnitFormatter = (value) => `${value}`;
